import React, { useCallback, useEffect, useMemo, useState } from 'react';
import quizApi from '../utils/quizApi';
import { formatStamp } from '../utils/datetime';

/**
 * One quiz's results — the QA manager's half of the module.
 *
 * The screen is built around a number the API is careful about and this component
 * must be equally careful with: **who owes this quiz**. Pass rate, average score and
 * question difficulty are all measured over the people who sat it, and every one of
 * them reads perfectly while forty people who owe the document have never opened it.
 * So the audience is the headline denominator, and when it can't be determined the
 * screen says so instead of printing a confident zero.
 *
 * Nothing is computed here that the server already decided. Statuses, rates and
 * per-question tallies arrive rolled up (`quiz_engine`), because a second copy of
 * "did this person pass" living in the browser is a copy that drifts.
 */

const STATUS_PILL = {
  passed: ['qz-pill-done', 'Passed'],
  failed: ['qz-pill-miss', 'Failed'],
  exhausted: ['qz-pill-miss', 'No attempts left'],
  in_progress: ['qz-pill-open', 'In progress'],
  not_started: ['qz-pill-draft', 'Not started'],
  blocked: ['qz-pill-lock', 'Waiting on signature'],
};

const TRAINING_LABEL = {
  done: 'Task signed',
  open: 'Task open',
  miss: 'No task',
};

const FILTERS = [
  ['', 'Everyone'],
  ['passed', 'Passed'],
  ['failed', 'Failed'],
  ['exhausted', 'No attempts left'],
  ['in_progress', 'In progress'],
  ['not_started', 'Not started'],
  ['blocked', 'Waiting on signature'],
];

/* Timestamps are stored by SQLite as a naive UTC string, and shown as UTC — `formatStamp`
   appends the `Z` the value means before parsing it. Reading one as local time (which is what
   `new Date('2026-08-28 22:40:11')` does) moves an attempt across a day boundary depending on
   who is looking at it; on a compliance record that is not a rounding error. `day` stays a
   slice: it feeds a CSV column, where the UTC day is already the right answer. */
const stamp = (s) => formatStamp(s);
const day = (s) => (s ? String(s).slice(0, 10) : '—');

function QuizResults({ quizId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [openRow, setOpenRow] = useState(null);
  // Which sitting's paper is open, and the papers already fetched. A submitted
  // attempt is frozen — its answers can never change — so one fetch per attempt is
  // cached for the life of the screen and reopening it costs nothing.
  const [openAttempt, setOpenAttempt] = useState(null);
  const [sheets, setSheets] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await quizApi.results(quizId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // `quizId` only — a callback prop in here would re-fetch on every parent render.
  }, [quizId]);

  useEffect(() => { load(); }, [load]);

  /* Open one sitting's marked paper. Fetched on demand — a quiz with 200 sittings
     would otherwise ship every answer of every attempt to draw one table — and only
     once per attempt, because a submitted attempt is frozen by definition. */
  const toggleAttempt = useCallback(async (attemptId) => {
    setOpenAttempt((cur) => (cur === attemptId ? null : attemptId));
    let seen = false;
    setSheets((cur) => {
      seen = Boolean(cur[attemptId]);
      return seen ? cur : { ...cur, [attemptId]: { loading: true } };
    });
    if (seen) return;
    try {
      const res = await quizApi.attemptAnswers(quizId, attemptId);
      setSheets((cur) => ({ ...cur, [attemptId]: { answers: res.answers } }));
    } catch (e) {
      // Kept on the attempt rather than raised to the page: a paper that won't load
      // must not blank out the results the manager came for.
      setSheets((cur) => ({ ...cur, [attemptId]: { error: e.message } }));
    }
  }, [quizId]);

  const openPerson = (userId) => {
    setOpenRow((cur) => (cur === userId ? null : userId));
    setOpenAttempt(null);
  };

  const people = useMemo(
    () => (data?.people || []).filter((p) => !filter || p.status === filter),
    [data, filter],
  );

  const downloadCsv = () => {
    const head = ['Name', 'User ID', 'Role', 'Training task', 'Result', 'Attempts',
      'Attempts allowed', 'Best score %', 'Last score %', 'Passed on', 'Record'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(',')].concat(
      (data.people || []).map((p) => [
        p.name, p.user_id, p.role, TRAINING_LABEL[p.training_state] || '',
        (STATUS_PILL[p.status] || ['', p.status])[1], p.attempts, p.attempts_allowed,
        p.best_score ?? '', p.last_score ?? '', day(p.passed_at), p.record_state,
      ].map(esc).join(',')),
    );
    const blob = new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quiz-results-${data.quiz.doc_key}${data.quiz.revision ? `-${data.quiz.revision}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="qz-loading">Loading results…</div>;
  if (error) return <div className="qz-note qz-note-err"><span>{error}</span></div>;
  if (!data) return null;

  const s = data.summary;
  const known = data.audience_known;
  const maxBand = Math.max(1, ...data.bands.map((b) => b.n));

  return (
    <div className="qz-results">
      {/* The denominator is either trustworthy or it isn't, and the difference decides
          what every percentage below means. Said first, not in a footnote. */}
      {!known && (
        <div className="qz-note qz-note-warn">
          <span>⚠</span>
          <div>
            <b>Who owes this quiz can’t be worked out, so the figures below cover only
            the people who have sat it.</b>{' '}
            {data.audience.message}
          </div>
        </div>
      )}

      {s.records_pending > 0 && (
        <div className="qz-note qz-note-warn">
          <span>⏳</span>
          <div>
            <b>
              {s.records_pending === 1
                ? '1 pass has not reached Orcanos yet.'
                : `${s.records_pending} passes have not reached Orcanos yet.`}
            </b>{' '}
            The quiz was passed here, but the training record is still queued — passing
            and being signed are two different facts, and only the first has happened.
          </div>
        </div>
      )}

      <div className="qz-kpis">
        <div className="qz-kpi ok">
          <div className="v">{s.passed}{known ? <span className="of"> / {s.people}</span> : null}</div>
          <div className="l">Passed</div>
          <div className="s">
            {known
              ? `${s.coverage ?? 0}% of everyone who owes this quiz`
              : `of ${s.sat} who have sat it`}
          </div>
        </div>

        {known && (
          <div className="qz-kpi due">
            <div className="v">{s.not_started}</div>
            <div className="l">Not started</div>
            <div className="s">Signed their task — the quiz is open to them</div>
          </div>
        )}

        {known && s.blocked > 0 && (
          <div className="qz-kpi">
            <div className="v">{s.blocked}</div>
            <div className="l">Waiting on signature</div>
            <div className="s">Can’t sit it until the training task is signed</div>
          </div>
        )}

        <div className="qz-kpi bad">
          <div className="v">{s.failed + s.exhausted}</div>
          <div className="l">Not passed</div>
          <div className="s">
            {s.exhausted
              ? `${s.exhausted} out of attempts — they need you to reopen it`
              : 'All still have attempts left'}
          </div>
        </div>

        <div className="qz-kpi quiz">
          <div className="v">{s.avg_score == null ? '—' : `${s.avg_score}%`}</div>
          <div className="l">Average score</div>
          <div className="s">
            Over {s.submitted} submitted attempt{s.submitted === 1 ? '' : 's'}
            {s.pass_rate != null ? ` · ${s.pass_rate}% of finishers passed` : ''}
          </div>
        </div>

        <div className="qz-kpi">
          <div className="v">{s.attempts}</div>
          <div className="l">Attempts</div>
          <div className="s">
            {s.sat} {s.sat === 1 ? 'person has' : 'people have'} sat it
            {s.in_progress ? ` · ${s.in_progress} in progress now` : ''}
          </div>
        </div>
      </div>

      {/* Scores, spread. A band is coloured as passing only when its LOW end clears
          the pass mark, so a band straddling it never reads as a pass. */}
      {s.submitted > 0 && (
        <div className="qz-panel qz-dist">
          <div className="qz-panel-head">
            Scores <span className="qz-meta">pass mark {data.quiz.pass_mark}%</span>
          </div>
          <div className="qz-panel-body">
            <div className="qz-bands">
              {data.bands.map((b) => (
                <div className="qz-band" key={b.lo}>
                  <div className="qz-band-col">
                    <span
                      className={`qz-band-fill${b.passing ? ' pass' : ''}`}
                      style={{ height: `${(b.n / maxBand) * 100}%` }}
                    />
                  </div>
                  <div className="qz-band-n">{b.n || ''}</div>
                  <div className="qz-band-l">{b.lo}–{b.hi}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------ people */}

      <div className="qz-section-head" style={{ marginTop: 22 }}>
        <h3>People</h3>
        <span className="qz-section-sub">
          {known
            ? 'Everyone who owes this quiz, plus anyone who has sat it.'
            : 'Everyone who has sat this quiz.'}
        </span>
        <button className="qz-btn qz-btn-ghost qz-btn-sm" style={{ marginLeft: 'auto' }}
                onClick={downloadCsv} disabled={!data.people.length}>
          Download CSV
        </button>
      </div>

      <div className="qz-toolbar">
        {FILTERS.map(([key, label]) => {
          const n = key ? (s[key] || 0) : data.people.length;
          if (key && !n) return null;
          return (
            <button
              key={key || 'all'}
              className={`qz-chip${filter === key ? ' active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label}<span className="qz-chip-n">{n}</span>
            </button>
          );
        })}
      </div>

      {people.length === 0 ? (
        <div className="qz-section-empty">
          {data.people.length === 0
            ? 'Nobody has sat this quiz yet, and the training report lists nobody who owes it.'
            : 'Nobody in this state.'}
        </div>
      ) : (
        <div className="qz-tablewrap">
          <table className="qz-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Training task</th>
                <th>Result</th>
                <th className="qz-num">Attempts</th>
                <th className="qz-num">Best</th>
                <th>Last attempt</th>
                {/* Passing here and being signed in Orcanos are two different facts
                    (Critical Note #29) — the hint says which one this column is. */}
                <th title="Whether the pass has been written back to Orcanos as a signed training record. Passing the quiz here does not sign the task.">
                  Record
                </th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const [cls, label] = STATUS_PILL[p.status] || STATUS_PILL.not_started;
                const open = openRow === p.user_id;
                return (
                  <React.Fragment key={p.user_id}>
                    <tr
                      className={p.history.length ? 'qz-clickable' : undefined}
                      onClick={() => p.history.length && openPerson(p.user_id)}
                    >
                      <td>
                        <div className="qz-name">
                          {p.history.length ? <span className="qz-caret">{open ? '▾' : '▸'}</span> : null}
                          {p.name}
                        </div>
                        <div className="qz-meta">{p.user_id}</div>
                        {/* Sat the quiz but isn't in the report's audience — usually a role
                            that changed after the last build. Flagged, never dropped and
                            never guessed into a role. */}
                        {!p.in_audience && (
                          <div className="qz-blocked-why">Not in the training report</div>
                        )}
                      </td>
                      <td>{p.role || <span className="qz-meta">—</span>}</td>
                      <td>
                        {p.training_state
                          ? <span className="qz-meta">{TRAINING_LABEL[p.training_state] || p.training_state}</span>
                          : <span className="qz-meta">—</span>}
                      </td>
                      <td><span className={`qz-pill ${cls}`}>{label}</span></td>
                      <td className="qz-num">
                        {p.attempts}<span className="qz-meta"> / {p.attempts_allowed}</span>
                        {p.in_progress ? <div className="qz-meta">1 open</div> : null}
                      </td>
                      <td className="qz-num">
                        {p.best_score == null ? <span className="qz-meta">—</span> : `${p.best_score}%`}
                      </td>
                      <td>{p.last_at ? <span className="qz-meta">{stamp(p.last_at)}</span>
                                     : <span className="qz-meta">—</span>}</td>
                      <td>
                        {p.record_state === 'written' && (
                          <span className="qz-pill qz-pill-done"
                                title="The training record was written to Orcanos.">
                            In Orcanos
                          </span>
                        )}
                        {p.record_state === 'pending' && (
                          <span className="qz-pill qz-pill-open"
                                title="Passed here, but the training record has not reached Orcanos yet. The pass is kept and shown until it does.">
                            Queued
                          </span>
                        )}
                        {p.record_state === 'failed' && (
                          <>
                            <span className="qz-pill qz-pill-miss"
                                  title="Writing the training record to Orcanos failed. The pass stays queued.">
                              Write failed
                            </span>
                            {p.record_error ? <div className="qz-blocked-why">{p.record_error}</div> : null}
                          </>
                        )}
                        {!p.record_state && <span className="qz-meta">—</span>}
                      </td>
                    </tr>
                    {open && (
                      <tr className="qz-subrow">
                        <td colSpan={8}>
                          <div className="qz-attempts">
                            {p.history.map((h) => {
                              /* Only a SUBMITTED attempt has a paper to show. An
                                 attempt in progress is half-answered, and the server
                                 refuses it for the same reason the per-question
                                 stats exclude it — those are a trainee's live
                                 selections, not a record. */
                              const readable = h.id && h.status !== 'in_progress';
                              const shown = openAttempt === h.id;
                              const sheet = sheets[h.id];
                              return (
                                <React.Fragment key={h.attempt_no}>
                                  <div
                                    className={`qz-attempt${readable ? ' qz-clickable' : ''}`}
                                    onClick={() => readable && toggleAttempt(h.id)}
                                  >
                                    <span className="qz-attempt-n">#{h.attempt_no}</span>
                                    <span className={`qz-pill ${
                                      h.status === 'passed' ? 'qz-pill-done'
                                        : h.status === 'failed' ? 'qz-pill-miss' : 'qz-pill-open'}`}>
                                      {h.status === 'in_progress' ? 'In progress' : h.status === 'passed' ? 'Passed' : 'Failed'}
                                    </span>
                                    <b>{h.score_pct == null ? '—' : `${h.score_pct}%`}</b>
                                    <span className="qz-meta">
                                      started {stamp(h.started_at)}
                                      {h.submitted_at ? ` · submitted ${stamp(h.submitted_at)}` : ''}
                                    </span>
                                    {readable && (
                                      <span className="qz-attempt-open">
                                        {shown ? '▾ Hide answers' : '▸ View answers'}
                                      </span>
                                    )}
                                  </div>
                                  {shown && (
                                    <div className="qz-sheet">
                                      {sheet?.loading && (
                                        <div className="qz-meta">Loading answers…</div>
                                      )}
                                      {sheet?.error && (
                                        <div className="qz-note qz-note-err">
                                          <span>{sheet.error}</span>
                                        </div>
                                      )}
                                      {sheet?.answers?.map((q) => (
                                        <div
                                          className={`qz-sheet-q${q.correct ? ' correct' : ''}`}
                                          key={q.id}
                                        >
                                          <div className="qz-sheet-qhead">
                                            <span className="qz-q-n">{q.seq}</span>
                                            <div className="qz-qstat-text">{q.text}</div>
                                            {/* Unanswered is its own state, never folded
                                                into "wrong": one says they didn't know,
                                                the other that they never got there. */}
                                            <span className={`qz-pill ${q.correct
                                              ? 'qz-pill-done'
                                              : q.answered ? 'qz-pill-miss' : 'qz-pill-draft'}`}>
                                              {q.correct ? 'Correct'
                                                : q.answered ? 'Wrong' : 'Left blank'}
                                            </span>
                                          </div>
                                          {q.options.map((o) => (
                                            <div
                                              className={`qz-sheet-opt${o.is_correct ? ' key' : ''}${o.chosen ? ' picked' : ''}`}
                                              key={o.id}
                                            >
                                              {/* Two independent marks, deliberately:
                                                  what they picked, and what the answer
                                                  was. One combined glyph would hide the
                                                  case that matters most — a wrong pick
                                                  beside the right answer. */}
                                              <span className="qz-sheet-pick">
                                                {o.chosen ? (o.is_correct ? '✓' : '✗') : ''}
                                              </span>
                                              <span className="qz-sheet-opt-text">{o.text}</span>
                                              {o.is_correct && (
                                                <span className="qz-sheet-key">Correct answer</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --------------------------------------------------------- questions */}

      <div className="qz-section-head" style={{ marginTop: 26 }}>
        <h3>Questions</h3>
        <span className="qz-section-sub">
          How each question performed over {s.submitted} submitted attempt
          {s.submitted === 1 ? '' : 's'}. An attempt still in progress is not counted —
          its answers are half-finished, and would report a question as harder than it is.
        </span>
      </div>

      {s.submitted === 0 ? (
        <div className="qz-section-empty">
          No submitted attempts yet, so there is nothing to say about the questions.
        </div>
      ) : (
        data.questions.map((q) => (
          <div className="qz-q qz-qstat" key={q.id}>
            <div className="qz-q-head">
              <span className="qz-q-n">{q.seq}</span>
              <div className="qz-qstat-text">{q.text}</div>
              <div className="qz-q-tools">
                {q.origin === 'ai' && <span className="qz-pill qz-pill-quiz">AI draft</span>}
                {/* A question most people get wrong is more often a badly written
                    question than a badly trained workforce, so it is flagged for the
                    author to re-read rather than left as a statistic. */}
                {q.pct != null && q.pct < 50 && (
                  <span className="qz-pill qz-pill-miss">Often missed</span>
                )}
                <span className="qz-qstat-pct">{q.pct == null ? '—' : `${q.pct}%`}</span>
              </div>
            </div>
            <div className="qz-meta" style={{ marginLeft: 32, marginBottom: 8 }}>
              {q.correct} of {q.asked} answered correctly
              {q.unanswered ? ` · ${q.unanswered} left blank` : ''}
            </div>
            {q.options.map((o) => (
              <div className={`qz-optstat${o.is_correct ? ' correct' : ''}`} key={o.id}>
                <span className="qz-optstat-mark">{o.is_correct ? '✓' : ''}</span>
                <span className="qz-optstat-text">{o.text}</span>
                <span className="qz-bar qz-optstat-bar">
                  <span style={{ width: `${o.pct}%` }} />
                </span>
                <span className="qz-optstat-n">{o.chosen}</span>
              </div>
            ))}
          </div>
        ))
      )}

      {data.audience?.built_at && (
        <p className="qz-meta" style={{ marginTop: 18 }}>
          Who owes this quiz is read from the training report
          {data.audience.panel_name ? <> for <b>{data.audience.panel_name}</b></> : null},
          built <b>{data.audience.built_at}</b>. Attempts are live.
        </p>
      )}
    </div>
  );
}

export default QuizResults;
